import { Schema } from "effect";
import { PapersFlowBoundaryParseOptions } from "./standard";
import { utf8ByteLength } from "./utf8";

export const DESKTOP_AUTH_SESSION_MAX_COOKIE_BYTES = 32 * 1_024;

const hasControlCharacter = (value: string): boolean => {
  for (const character of value) {
    const codePoint = character.codePointAt(0) ?? 0;
    if (codePoint <= 0x1f || (codePoint >= 0x7f && codePoint <= 0x9f)) return true;
  }
  return false;
};

export const DesktopAuthSessionCookieSchema = Schema.String.check(
  Schema.isMinLength(1),
  Schema.makeFilter(
    (value) =>
      (!hasControlCharacter(value) &&
        utf8ByteLength(value) <= DESKTOP_AUTH_SESSION_MAX_COOKIE_BYTES) ||
      `must be a bounded desktop authentication cookie of at most ${DESKTOP_AUTH_SESSION_MAX_COOKIE_BYTES} UTF-8 bytes`,
  ),
);

export const DesktopAuthSessionLoadResultSchema = Schema.Struct({
  version: Schema.Literal(1),
  cookie: Schema.NullOr(DesktopAuthSessionCookieSchema),
});

export const DesktopAuthSessionStoreInputSchema = Schema.Struct({
  version: Schema.Literal(1),
  cookie: Schema.NullOr(DesktopAuthSessionCookieSchema),
});

export const DesktopAuthSessionErrorCodeSchema = Schema.Literals([
  "invalid-input",
  "invalid-response",
  "secure-storage-unavailable",
]);

export const DesktopAuthSessionErrorPayloadSchema = Schema.Union([
  Schema.Struct({
    code: Schema.Literal("invalid-input"),
    message: Schema.Literal("The desktop authentication session is invalid."),
    retryable: Schema.Literal(false),
  }),
  Schema.Struct({
    code: Schema.Literal("invalid-response"),
    message: Schema.Literal("The secure desktop session store returned an invalid response."),
    retryable: Schema.Literal(false),
  }),
  Schema.Struct({
    code: Schema.Literal("secure-storage-unavailable"),
    message: Schema.Literal("The secure desktop session store is unavailable."),
    retryable: Schema.Literal(true),
  }),
]);

export type DesktopAuthSessionCookie = typeof DesktopAuthSessionCookieSchema.Type;
export type DesktopAuthSessionLoadResult = typeof DesktopAuthSessionLoadResultSchema.Type;
export type DesktopAuthSessionLoadResultEncoded = typeof DesktopAuthSessionLoadResultSchema.Encoded;
export type DesktopAuthSessionStoreInput = typeof DesktopAuthSessionStoreInputSchema.Type;
export type DesktopAuthSessionStoreInputEncoded = typeof DesktopAuthSessionStoreInputSchema.Encoded;
export type DesktopAuthSessionErrorCode = typeof DesktopAuthSessionErrorCodeSchema.Type;
export type DesktopAuthSessionErrorPayload = typeof DesktopAuthSessionErrorPayloadSchema.Type;
export type DesktopAuthSessionErrorPayloadEncoded =
  typeof DesktopAuthSessionErrorPayloadSchema.Encoded;

export const decodeDesktopAuthSessionCookie = Schema.decodeUnknownSync(
  DesktopAuthSessionCookieSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeDesktopAuthSessionLoadResult = Schema.decodeUnknownSync(
  DesktopAuthSessionLoadResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeDesktopAuthSessionLoadResult = Schema.encodeSync(
  DesktopAuthSessionLoadResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeDesktopAuthSessionStoreInput = Schema.decodeUnknownSync(
  DesktopAuthSessionStoreInputSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeDesktopAuthSessionStoreInput = Schema.encodeSync(
  DesktopAuthSessionStoreInputSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeDesktopAuthSessionErrorPayload = Schema.decodeUnknownSync(
  DesktopAuthSessionErrorPayloadSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeDesktopAuthSessionErrorPayload = Schema.encodeSync(
  DesktopAuthSessionErrorPayloadSchema,
  PapersFlowBoundaryParseOptions,
);
